"use client"

import { Avatar, AvatarFallback, AvatarImage } from "@repo/shad/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@repo/shad/ui/dropdown-menu"
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@repo/shad/ui/sheet"
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@repo/shad/ui/sidebar"
import { IconDotsVertical, IconLogout, IconUserCircle } from "@tabler/icons-react"
import { useCallback, useState } from "react"

import { ExpandableCard } from "../expandable-card"
import type { NavUserProps } from "../types"
import { avatarUrl } from "../utils/avatar-url"

export function NavUser({ user, onSignOut, menuItems, accountContent }: NavUserProps) {
  const { isMobile } = useSidebar()
  const [accountOpen, setAccountOpen] = useState(false)

  const initials = user?.name
    ?.split(" ")
    .map((name: string) => name.charAt(0))
    .join("")

  const handleClose = useCallback(() => setAccountOpen(false), [])
  const openAccount = useCallback(() => {
    window.setTimeout(() => setAccountOpen(true), 0)
  }, [])

  const dropdownMenu = (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <SidebarMenuButton
          size="lg"
          className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
        >
          <Avatar className="h-8 w-8 rounded-lg">
            <AvatarImage src={avatarUrl(user?.image, 32)} alt={user?.name} />
            <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
          </Avatar>
          <div className="grid flex-1 text-left text-sm leading-tight">
            <span className="truncate font-medium">{user?.name}</span>
            <span className="opacity-60 truncate text-xs">{user?.email}</span>
          </div>
          <IconDotsVertical className="ml-auto size-4" />
        </SidebarMenuButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
        side={isMobile ? "bottom" : "right"}
        align="end"
        sideOffset={4}
      >
        <DropdownMenuLabel className="p-0 font-normal">
          <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
            <Avatar className="h-8 w-8 rounded-lg">
              <AvatarImage src={avatarUrl(user?.image, 32)} alt={user?.name} />
              <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
            </Avatar>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-medium">{user?.name}</span>
              <span className="text-muted-foreground truncate text-xs">{user?.email}</span>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          {accountContent && (
            <DropdownMenuItem onClick={openAccount}>
              <IconUserCircle />
              Account
            </DropdownMenuItem>
          )}
          {menuItems}
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onSignOut}>
          <IconLogout />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )

  if (isMobile) {
    return (
      <ExpandableCard.Root selectedId={null} onClose={handleClose}>
        <SidebarMenu>
          <SidebarMenuItem>
            <ExpandableCard.Trigger cardId="account">{dropdownMenu}</ExpandableCard.Trigger>
          </SidebarMenuItem>
        </SidebarMenu>
        {accountContent && (
          <Sheet open={accountOpen} onOpenChange={(open) => !open && handleClose()}>
            <SheetContent
              side="bottom"
              className="h-[85vh] rounded-t-xl p-0 [&>button]:hidden"
              onCloseAutoFocus={() => {
                document.body.style.pointerEvents = ""
              }}
            >
              <SheetHeader className="sr-only">
                <SheetTitle>Account</SheetTitle>
                <SheetDescription>Manage your account</SheetDescription>
              </SheetHeader>
              <div className="overflow-y-auto h-full">{accountContent(handleClose)}</div>
            </SheetContent>
          </Sheet>
        )}
      </ExpandableCard.Root>
    )
  }

  return (
    <ExpandableCard.Root selectedId={accountOpen ? "account" : null} onClose={handleClose}>
      <SidebarMenu>
        <SidebarMenuItem>
          <ExpandableCard.Trigger cardId="account">{dropdownMenu}</ExpandableCard.Trigger>
        </SidebarMenuItem>
      </SidebarMenu>

      {accountContent && accountOpen && (
        <ExpandableCard.Overlay
          anchor
          className="w-[calc(100vw-16px)] max-w-md max-h-[80vh] overflow-y-auto"
        >
          {accountContent(handleClose)}
        </ExpandableCard.Overlay>
      )}
    </ExpandableCard.Root>
  )
}
